import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { NewKeyRevealModal } from "./PrintAgentTab";
import { KeyReveal } from "./KeyReveal";

/** "L-PFR licence" - posebne licence (lpfr_...) za preuzimanje i rad L-PFR-a; nisu isto što i ključ Print Agent-a. */
export function LpfrLicensesSection() {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [reveal, setReveal] = useState<string | null>(null);

  const list = useQuery({ queryKey: ["lpfr-licenses"], queryFn: () => api.listLpfrLicenses() });
  const create = useMutation({
    mutationFn: () => api.createLpfrLicense(label.trim() || undefined),
    onSuccess: (r) => {
      setLabel("");
      setReveal(r.key);
      qc.invalidateQueries({ queryKey: ["lpfr-licenses"] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeLpfrLicense(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lpfr-licenses"] }),
  });

  const active = (list.data ?? []).filter((k) => !k.revoked_at);

  return (
    <div className="space-y-3 border-t border-border pt-5">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">L-PFR licence</h2>
        <p className="text-xs text-slate-400">
          Licenca počinje sa lpfr_ i služi samo za preuzimanje i rad L-PFR-a (lokalni fiskalni procesor). Pun ključ je uvek vidljiv ispod svake licence.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Naziv (npr. Kasa 1 – Windows)"
          className="h-9 w-72 rounded-lg border border-border px-3 text-sm"
        />
        <Button size="sm" onClick={() => create.mutate()} disabled={create.isPending}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Nova L-PFR licenca
        </Button>
      </div>
      {create.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {create.error instanceof Error ? create.error.message : "Pravljenje licence nije uspelo."}
        </p>
      )}

      {list.isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : active.length === 0 ? (
        <p className="text-sm text-slate-500">Još nema L-PFR licenci.</p>
      ) : (
        <div className="divide-y divide-border rounded-xl border border-border bg-surface">
          {active.map((k) => (
            <div key={k.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
              <div>
                <span className="font-medium text-slate-800">{k.label || "Bez naziva"}</span>{" "}
                <code className="text-xs text-slate-500">{k.key_prefix}…</code>
                <KeyReveal value={k.key} />
                <div className="text-xs text-slate-400">
                  napravljena {new Date(k.created_at).toLocaleDateString("sr-Latn-RS")}
                  {k.last_used_at && ` · poslednje korišćenje ${new Date(k.last_used_at).toLocaleDateString("sr-Latn-RS")}`}
                </div>
              </div>
              <button
                type="button"
                className="text-slate-400 hover:text-red-600"
                aria-label="Opozovi"
                onClick={() => window.confirm("Opozvati ovu licencu? L-PFR koji je koristi više neće moći da se preuzme.") && revoke.mutate(k.id)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}

      {reveal && (
        <NewKeyRevealModal
          rawKey={reveal}
          title="Nova L-PFR licenca"
          hint="Unesite je u L-PFR pri instalaciji ili je koristite na adresi za preuzimanje."
          onClose={() => setReveal(null)}
        />
      )}
    </div>
  );
}
